/**
 * The client half of Google sign-in: where to send the browser, where to send it back to, and
 * how to say what went wrong.
 *
 * Deliberately not a fetch. `GET /auth/google` answers with a 302 to Google and the far end
 * comes back as a top-level navigation carrying the session cookie, which only a real page
 * navigation can do — so the button is an `<a href>` and this module is just strings.
 */

import { API_BASE_URL } from '@/lib/api';

/** The server's own route, not a tRPC procedure — a redirect dance has no RPC shape. */
export const GOOGLE_SIGN_IN_URL = `${API_BASE_URL}/auth/google`;

/**
 * Where a finished sign-in lands.
 *
 * A closed set rather than any path: the value survives the round trip through Google in
 * session storage, and a redirect target that a stored string can name is one bug away from an
 * open redirect.
 *
 * **BOTH SIGN-IN PAGES ASK FOR `'/'`, AND THAT IS THE RULE RATHER THAN THE DEFAULT.** Signing in
 * must never by itself drop somebody into a match — they came to sign in, not to play — so the
 * landing is the lobby and starting a game is the press they make next. `'/play'` stays in the
 * set because it is what an *explicitly requested* destination looks like: a caller that already
 * knows where the player was going says so, and this is where it would be said.
 */
export type ReturnTo = '/play' | '/';

const RETURN_TO_KEY = 'chompdome-oauth-return-to';

/** Remember where to land, just before handing the browser over to the server. */
export function rememberReturnTo(returnTo: ReturnTo): void {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  } catch {
    // Storage disabled or full. Home is a fine place to land, so this is not worth a failure.
  }
}

/** Read it back and forget it — one sign-in, one landing. */
export function takeReturnTo(): ReturnTo {
  try {
    const stored = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    if (stored === '/play') return stored;
  } catch {
    // Same as above: fall through to home.
  }
  return '/';
}

/**
 * The `reason` on a failed redirect, in words.
 *
 * The server answers with a code precisely so the detail — an email address, Google's own error
 * body — stays out of a URL the browser keeps in its history. Turning it back into a sentence is
 * this side's job, and an unknown code is still a sentence rather than a blank screen.
 */
const FAILURE_MESSAGES: Record<string, string> = {
  declined: 'Google sign-in was cancelled.',
  email_unverified:
    'That Google account has no verified email address, so it cannot be used to sign in.',
  account_disabled: 'That account has been disabled.',
  // Deliberately without the reason or the expiry: neither travels on this redirect (see
  // `FAILURE_REASONS` in the server's oauth plugin). Signing in with the password form is what
  // shows the moderator's own sentence and the date it ends.
  account_banned:
    'That account is banned. Sign in with your email and password to see why and until when.',
  email_taken:
    'An account already exists with that email address. Sign in with your password instead.',
  state_mismatch: 'That sign-in attempt expired. Please try again.',
  invalid_request: 'That sign-in attempt expired. Please try again.',
  token_exchange_failed: 'We could not reach Google. Please try again.',
  invalid_id_token: "Google's answer could not be verified. Please try again.",
};

export function googleFailureMessage(reason: string | undefined): string {
  const known = reason === undefined ? undefined : FAILURE_MESSAGES[reason];
  return known ?? 'Google sign-in did not finish. Please try again.';
}
